import { useState } from 'react'
import { MoreHoriz, Repeat } from '@mui/icons-material'
import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Switch, Typography } from '@mui/material'
import { BeoflowButton } from '@/components/BeoflowButton'
import { useRoutineScope } from '@/components/routines/routineScope'
import { useDeleteRoutine, useRoutines, useUpdateRoutine } from '@/services/api/routines'
import { beoflowColors, layout, opacity, spacing, typography } from '@/theme/muiTheme'

function RoutineItem({ routine, label }: { routine: { id: number; name: string; enabled: boolean }; label: string | null }) {
  const updateRoutine = useUpdateRoutine()
  const deleteRoutine = useDeleteRoutine()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const isPending = updateRoutine.isPending || deleteRoutine.isPending

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm,
          py: spacing.sm,
          borderBottom: `${layout.borderDefault} solid ${beoflowColors.beoWhite}${opacity.separator}`,
        }}
      >
        <Repeat color={routine.enabled ? 'info' : 'disabled'} sx={{ fontSize: layout.iconSmall }} aria-hidden />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap color="text.secondary" sx={{ fontSize: typography.fontSizeXs, fontWeight: typography.fontWeightMedium }}>
            {routine.name}
          </Typography>
          {label && (
            // Against an athlete this list mixes the viewer's routines with the
            // athlete's own, and Delete is one click away: say whose each one is.
            <Typography variant="monoSmall" noWrap display="block" sx={{ color: beoflowColors.tealBase }}>
              {label}
            </Typography>
          )}
        </Box>
        <Switch
          size="small"
          checked={routine.enabled}
          disabled={isPending}
          onChange={() => updateRoutine.mutate({ id: routine.id, enabled: !routine.enabled })}
          slotProps={{ input: { 'aria-label': `${routine.enabled ? 'Pause' : 'Resume'} ${routine.name}` } }}
        />
        <IconButton size="small" aria-label={`Actions for ${routine.name}`} onClick={(event) => setAnchor(event.currentTarget)}>
          <MoreHoriz sx={{ fontSize: layout.iconSmall }} />
        </IconButton>
      </Box>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem
          disabled={isPending}
          onClick={() => {
            setAnchor(null)
            setConfirmingDelete(true)
          }}
          sx={{ color: beoflowColors.peakOrange }}
        >
          Delete routine
        </MenuItem>
      </Menu>

      <Dialog open={confirmingDelete} onClose={() => setConfirmingDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete routine?</DialogTitle>
        <DialogContent>
          <Typography>Flow will stop running “{routine.name}”. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <BeoflowButton variant="text" onClick={() => setConfirmingDelete(false)}>Cancel</BeoflowButton>
          <BeoflowButton
            variant="textSecondary"
            disabled={isPending}
            onClick={() => deleteRoutine.mutate(routine.id, { onSuccess: () => setConfirmingDelete(false) })}
          >
            Delete
          </BeoflowButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default function RoutineSidebarSection({ targetProfileId, limitHeight = true }: { targetProfileId?: number | null; limitHeight?: boolean }) {
  const { data: routines, isLoading, isError, refetch } = useRoutines(targetProfileId)
  const scopeFor = useRoutineScope(targetProfileId)

  return (
    <Box sx={{ px: spacing.md, ...(limitHeight ? { flexShrink: 0, maxHeight: '40%', overflowY: 'auto' } : {}) }}>
      <Typography variant="monoLabel" sx={{ color: 'text.secondary', py: spacing.sm }}>
        Routines
      </Typography>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: spacing.md }}><CircularProgress size={layout.iconSmall} color="info" /></Box>
      ) : isError && !routines ? (
        // Not "No routines yet", and not a failed refetch either: in
        // react-query v5 a background refetch failure keeps the last good list.
        <Box sx={{ pb: spacing.sm }}>
          <Typography variant="body2" color="text.secondary">Could not load routines.</Typography>
          <BeoflowButton variant="text" size="small" onClick={() => void refetch()} sx={{ mt: spacing.xs }}>
            Try again
          </BeoflowButton>
        </Box>
      ) : routines?.length ? (
        routines.map((routine) => <RoutineItem key={routine.id} routine={routine} label={scopeFor(routine).label} />)
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ pb: spacing.sm }}>No routines yet.</Typography>
      )}
    </Box>
  )
}
